import axios from 'axios';
import { constants } from '../modules/Constants';

const API_BASE_URL = constants.API_URL;

const unwrap = (response) => {
  const data = response?.data;
  if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'data')) {
    return data.data;
  }
  return data;
};

class MessageService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/messages`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests - use same token key as APIService
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('@auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Get user's conversations (inbox)
   */
  async getConversations(page = 1, limit = 20, options = {}) {
    try {
      const response = await this.api.get('/', {
        params: { page, limit },
        signal: options?.signal,
      });

      // Backend returns: { data: { data: [...], meta: {...} } }
      const responseData = response.data?.data || [];
      const meta = response.data?.meta || {};
      
      return {
        conversations: Array.isArray(responseData) ? responseData : [],
        total: meta.total || 0,
        page: meta.page || page,
        limit: meta.limit || limit,
        totalPages: meta.totalPages || Math.ceil((meta.total || 0) / (meta.limit || limit))
      };
    } catch (error) {
      // Suppress noisy logs for request cancellations (AbortController)
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        throw error;
      }
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  /**
   * Get a single conversation with messages
   */
  async getConversation(conversationId, page = 1, limit = 50, options = {}) {
    try {
      const response = await this.api.get(`/${conversationId}`, {
        params: { page, limit },
        signal: options?.signal,
      });
      
      // Backend returns either:
      // - { ...conversation, messages: [...], meta: {...} }
      // - { data: { data: {...}, meta: {...} } }
      const rawData = response.data?.data?.data || response.data?.data || response.data || {};
      const meta = response.data?.data?.meta || response.data?.meta || rawData?.meta || {};
      
      console.log('MessageService.getConversation - rawData.name:', rawData?.name);
      console.log('MessageService.getConversation - rawData.type:', rawData?.type);
      
      return {
        ...rawData,
        messages: Array.isArray(rawData.messages) ? rawData.messages : [],
        total: meta.total || 0,
        page: meta.page || page,
        limit: meta.limit || limit,
        totalPages: meta.totalPages || Math.ceil((meta.total || 0) / (meta.limit || limit))
      };
    } catch (error) {
      // Don't spam console when a request was intentionally cancelled by AbortController
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        throw error;
      }
      // If the server returned 403 (forbidden), caller will handle cleanup; avoid logging here to reduce noise
      if (error?.response?.status === 403) {
        throw error;
      }
      console.error('Error fetching conversation:', error);
      throw error;
    }
  }

  /**
   * Create a new conversation
   */
  async createConversation(participants, name = null, type = 'direct', options = {}) {
    try {
      const payload = {
        participants,
        name,
        type,
        ...options,
      };
      
      const response = await this.api.post('/', payload);
      return unwrap(response);
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  /**
   * Send a message
   */
  async sendMessage(conversationId, content, type = 'text', metadata = {}, replyTo = null) {
    try {
      const payload = {
        conversationId,
        type,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        replyTo,
      };
      
      // Different message types expect different field names
      if (type === 'emoji') {
        payload.emoji = content;
      } else if (type === 'image' || type === 'gif' || type === 'voice') {
        payload.url = content;
        if (type === 'gif' && metadata.width && metadata.height) {
          payload.width = metadata.width;
          payload.height = metadata.height;
        }
        if (type === 'voice' && metadata.duration) {
          payload.duration = metadata.duration;
        }
      } else {
        payload.content = content;
      }
      
      const response = await this.api.post(`/${conversationId}`, payload);
      return unwrap(response);
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Send a media message (image upload)
   */
  async sendMediaMessage(conversationId, file, caption = '', type = 'image', metadata = null) {
    try {
      const formData = new FormData();
      
      // For image uploads
      if (file) {
        formData.append('file', file);
        if (caption) {
          formData.append('caption', caption);
        }
      }

      const response = await this.api.post(`/conversations/${conversationId}/messages/media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return unwrap(response);
    } catch (error) {
      console.error('Error sending media message:', error);
      throw error;
    }
  }

  /**
   * Send a GIF message
   */
  async sendGifMessage(conversationId, gifUrl, width, height, caption = '') {
    try {
      const payload = {
        url: gifUrl,
        width,
        height,
        caption: caption || '',
      };
      
      const response = await this.api.post(`/conversations/${conversationId}/messages/gif`, payload);
      return unwrap(response);
    } catch (error) {
      console.error('Error sending GIF message:', error);
      throw error;
    }
  }

  /**
   * Mark message as read
   */
  async markAsRead(conversationId, messageId) {
    try {
      const response = await this.api.put(`/${conversationId}/${messageId}/read`);
      return unwrap(response);
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  /**
   * Mark all messages in conversation as read
   */
  async markAllAsRead(conversationId) {
    try {
      const response = await this.api.put(`/conversations/${conversationId}/read-all`);
      return unwrap(response);
    } catch (error) {
      // Suppress logging for request cancellations and for 403 Forbidden
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        throw error;
      }
      if (error?.response?.status === 403) {
        // Caller should handle removal/cleanup. Don't spam console with 403 logs.
        throw error;
      }
      console.error('Error marking all as read:', error);
      throw error;
    }
  }

  /**
   * Search messages
   */
  async searchMessages(conversationId, query, page = 1, limit = 20) {
    try {
      const response = await this.api.get(`/conversations/${conversationId}/search`, {
        params: { query, page, limit }
      });
      
      return unwrap(response);
    } catch (error) {
      console.error('Error searching messages:', error);
      throw error;
    }
  }

  /**
   * Mute conversation
   */
  async muteConversation(conversationId, duration = '1d') {
    try {
      const response = await this.api.post(`/conversations/${conversationId}/mute`, { duration });
      return unwrap(response);
    } catch (error) {
      console.error('Error muting conversation:', error);
      throw error;
    }
  }

  /**
   * Unmute conversation
   */
  async unmuteConversation(conversationId) {
    try {
      const response = await this.api.post(`/conversations/${conversationId}/unmute`);
      return unwrap(response);
    } catch (error) {
      console.error('Error unmuting conversation:', error);
      throw error;
    }
  }

  /**
   * Delete conversation
   */
  async deleteConversation(conversationId, deleteType = 'for_me', reason = '') {
    try {
      const response = await this.api.delete(`/conversations/${conversationId}`, {
        data: { deleteType, reason }
      });
      return unwrap(response);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }

  /**
   * Archive conversation
   */
  async archiveConversation(conversationId, archive = true) {
    try {
      const response = await this.api.post(`/conversations/${conversationId}/archive`, { archive });
      return unwrap(response);
    } catch (error) {
      console.error('Error archiving conversation:', error);
      throw error;
    }
  }

  /**
   * Get archived conversations
   */
  async getArchivedConversations(page = 1, limit = 20) {
    try {
      const response = await this.api.get('/archived', {
        params: { page, limit }
      });
      return unwrap(response);
    } catch (error) {
      console.error('Error fetching archived conversations:', error);
      throw error;
    }
  }

  /**
   * Delete message
   */
  async deleteMessage(messageId, deleteType = 'for_me', reason = '') {
    try {
      const response = await this.api.delete(`/messages/${messageId}`, {
        data: { deleteType, reason }
      });
      return unwrap(response);
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  /**
   * Edit message
   */
  async editMessage(messageId, content) {
    try {
      const response = await this.api.put(`/messages/${messageId}`, { content });
      return unwrap(response);
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  }

  /**
   * Add reaction to message
   */
  async addReaction(messageId, emoji, conversationId) {
    try {
      const response = await this.api.post(`/${conversationId}/messages/${messageId}/reactions`, { emoji });
      return unwrap(response);
    } catch (error) {
      console.error('Error adding reaction:', error);
      throw error;
    }
  }

  /**
   * Remove reaction from message
   */
  async removeReaction(messageId, emoji, conversationId) {
    try {
      const response = await this.api.delete(`/${conversationId}/messages/${messageId}/reactions`, {
        data: { emoji }
      });
      return unwrap(response);
    } catch (error) {
      console.error('Error removing reaction:', error);
      throw error;
    }
  }

  async getPinnedMessages(conversationId) {
    try {
      const response = await this.api.get(`/conversations/${conversationId}/pins`);
      return unwrap(response);
    } catch (error) {
      console.error('Error fetching pinned messages:', error);
      throw error;
    }
  }

  async pinMessage(conversationId, messageId) {
    try {
      const response = await this.api.post(`/conversations/${conversationId}/pins/${messageId}`);
      return unwrap(response);
    } catch (error) {
      console.error('Error pinning message:', error);
      throw error;
    }
  }

  async unpinMessage(conversationId, messageId) {
    try {
      const response = await this.api.delete(`/conversations/${conversationId}/pins/${messageId}`);
      return unwrap(response);
    } catch (error) {
      console.error('Error unpinning message:', error);
      throw error;
    }
  }
}

export default new MessageService();
